import React, { useCallback, useState } from 'react';
import baseStyles from '../../styles/ReporteCalificacionesStyles/ReporteCalificaciones.module.css';
import styles from '../../styles/MateriasEquivalentesStyles/MateriasEquivalentes.module.css';
import Sidebar, { useSidebar } from '../../components/Sidebar';
import HeaderGlobal from '../../components/HeaderGlobal';
import LoaderOverlay from '../../components/LoaderOverlay';
import FiltrosRevalidacion from './FiltrosRevalidacion';
import InfoEstudiante from './InfoEstudiante';
import TablaMateriasRevalidar from './TablaMateriasRevalidar';
import {
  getEstudianteParaRevalidacion,
  getMateriasCandidatasRevalidacion,
  guardarRevalidaciones,
} from '../../api/materiasRevalidar';
import { exportMateriasRevalidarCSV } from '../../utils/exportMateriasRevalidarCSV';

function MateriasEquivalentesScreen() {
  const { isCollapsed } = useSidebar();

  const [filters, setFilters] = useState({
    ciclo_escolar: '',
    id_campus: '',
    matricula: '',
  });
  const [contexto, setContexto] = useState({ ciclo: '', campus: '', matricula: '' });

  const [loadingBuscar, setLoadingBuscar] = useState(false);
  const [loadingGuardar, setLoadingGuardar] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [estudiante, setEstudiante] = useState(null);
  const [rows, setRows] = useState([]);

  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleBuscar = useCallback(async () => {
    setError('');
    setSuccess('');
    setEstudiante(null);
    setRows([]);
    if (!filters.matricula || !String(filters.matricula).trim()) {
      setError('Captura una matrícula para continuar.');
      return;
    }
    setLoadingBuscar(true);
    try {
      const est = await getEstudianteParaRevalidacion({
        matricula: String(filters.matricula).trim(),
        id_campus: filters.id_campus || undefined,
        ciclo_escolar: filters.ciclo_escolar || undefined,
      });
      setEstudiante(est);

      const mat = await getMateriasCandidatasRevalidacion({
        id_estudiante: est.id_estudiante,
      });
      const initial = (mat.resultados || []).map(r => ({
        ...r,
        // Normalizamos a strings vacíos para los inputs controlados
        rev_anio_ciclo_escolar: r.rev_anio_ciclo_escolar ?? '',
        rev_calificacion_final: r.rev_calificacion_final ?? '',
        rev_escuela_procedencia: r.rev_escuela_procedencia ?? '',
        rev_observaciones: r.rev_observaciones ?? '',
      }));
      setRows(initial);
    } catch (err) {
      console.error('[MateriasEquivalentesScreen] Error al buscar:', err);
      setError(err.message || 'No se pudo obtener la información del estudiante.');
    } finally {
      setLoadingBuscar(false);
    }
  }, [filters]);

  const handleChangeRow = useCallback((id_materia, key, value) => {
    setRows(prev => prev.map(r => (r.id_materia === id_materia ? { ...r, [key]: value } : r)));
    setSuccess('');
  }, []);

  const handleGuardar = useCallback(async () => {
    setError('');
    setSuccess('');
    if (!estudiante) {
      setError('Primero busca un estudiante.');
      return;
    }
    // Solo enviamos filas con calificación capturada
    const aEnviar = rows
      .filter(r => r.rev_calificacion_final !== '' && r.rev_calificacion_final != null)
      .map(r => ({
        id_materia: r.id_materia,
        rev_anio_ciclo_escolar: r.rev_anio_ciclo_escolar || null,
        rev_calificacion_final: r.rev_calificacion_final,
        rev_escuela_procedencia: r.rev_escuela_procedencia || null,
        rev_observaciones: r.rev_observaciones || null,
      }));

    if (aEnviar.length === 0) {
      setError('Captura al menos una calificación revalidada para guardar.');
      return;
    }

    setLoadingGuardar(true);
    try {
      const resp = await guardarRevalidaciones({
        id_estudiante: estudiante.id_estudiante,
        id_ciclo_escolar: filters.ciclo_escolar || undefined,
        registros: aEnviar,
      });

      let msg = `Guardado correctamente. Creados: ${resp.creados || 0}, actualizados: ${resp.actualizados || 0}.`;
      if (resp.errores && resp.errores.length) {
        msg += ` Con ${resp.errores.length} error(es), revisa la consola.`;
        console.warn('[MateriasEquivalentesScreen] Errores parciales:', resp.errores);
      }
      setSuccess(msg);

      // Refrescar listado
      const mat = await getMateriasCandidatasRevalidacion({
        id_estudiante: estudiante.id_estudiante,
      });
      const initial = (mat.resultados || []).map(r => ({
        ...r,
        rev_anio_ciclo_escolar: r.rev_anio_ciclo_escolar ?? '',
        rev_calificacion_final: r.rev_calificacion_final ?? '',
        rev_escuela_procedencia: r.rev_escuela_procedencia ?? '',
        rev_observaciones: r.rev_observaciones ?? '',
      }));
      setRows(initial);
    } catch (err) {
      console.error('[MateriasEquivalentesScreen] Error al guardar:', err);
      setError(err.message || 'No se pudieron guardar las revalidaciones.');
    } finally {
      setLoadingGuardar(false);
    }
  }, [rows, estudiante, filters]);

  const handleExportCSV = useCallback(() => {
    if (!estudiante || rows.length === 0) return;
    exportMateriasRevalidarCSV({
      filas: rows,
      contexto: {
        matricula: estudiante.matricula,
        nombre: estudiante.nombre_completo,
        programa: estudiante.programa_nombre,
        seccion: estudiante.seccion_nombre,
        campus: estudiante.campus_nombre || contexto.campus,
        ciclo: contexto.ciclo,
      },
    });
  }, [estudiante, rows, contexto]);

  const loading = loadingBuscar || loadingGuardar;
  const hasRows = rows.length > 0;

  return (
    <div className={baseStyles.mainContainer}>
      <div className={`${baseStyles.layout} ${isCollapsed ? baseStyles.sidebarCollapsedLayout : ''}`}>
        <div className={baseStyles.sidebarColumn}>
          <Sidebar />
        </div>
        <div className={baseStyles.contentColumn}>
          <main className={baseStyles.mainContent}>
            <HeaderGlobal title="Materias Equivalentes a Revalidar" />
            <div className={baseStyles.contentArea}>
              <div className={baseStyles.contentWrapper} style={{ position: 'relative' }}>
                <LoaderOverlay
                  visible={loading}
                  label={loadingGuardar ? 'Guardando revalidaciones...' : 'Buscando estudiante...'}
                />

                <FiltrosRevalidacion
                  filters={filters}
                  onChange={handleFilterChange}
                  onBuscar={handleBuscar}
                  loading={loadingBuscar}
                  onContextResolved={setContexto}
                />

                {error && <div className={styles.errorBanner}>{error}</div>}
                {success && <div className={styles.successBanner}>{success}</div>}

                {estudiante ? (
                  <>
                    <InfoEstudiante estudiante={estudiante} />

                    <div className={baseStyles.reportContainer}>
                      <TablaMateriasRevalidar
                        rows={rows}
                        onChangeRow={handleChangeRow}
                        readOnly={loadingGuardar}
                      />
                      <div className={baseStyles.actionsRow}>
                        <button
                          type="button"
                          className={baseStyles.xlsButton}
                          onClick={handleExportCSV}
                          disabled={!hasRows}
                          title={hasRows ? 'Exportar a CSV' : 'No hay datos para exportar'}
                        >
                          <span role="img" aria-label="csv">📄</span>
                          Exportar CSV
                        </button>
                        <button
                          type="button"
                          className={styles.saveButton}
                          onClick={handleGuardar}
                          disabled={!hasRows || loadingGuardar}
                          title="Guardar revalidaciones"
                        >
                          <span role="img" aria-label="guardar">💾</span>
                          {loadingGuardar ? 'Guardando...' : 'Guardar'}
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className={baseStyles.reportContainer}>
                    <div className={baseStyles.emptyState}>
                      <div className={baseStyles.emptyIcon}>🔁</div>
                      <p className={baseStyles.emptyText}>Sin estudiante seleccionado</p>
                      <p className={baseStyles.emptySubText}>
                        Captura una <strong>matrícula</strong> y presiona <strong>Buscar Estudiante</strong>.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

export default MateriasEquivalentesScreen;
